import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';

import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';
import { DataTable } from 'src/components/DataTable';
import { getWorkerColumns, getWorkerColumnsActive } from 'src/hooks/useWorkerColumns';
import { useParams } from 'react-router-dom';
import { WorkerScope } from 'src/api/hooks/useWorker.types';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { toggleWorkers } from 'src/utils/toggleWorkers';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { workersTableTranslations } from 'src/utils/workerUtils';
import { useWorkersData } from 'src/hooks/useWorkersData';
import { InviteActiveWorkersDialog } from './components/InviteActiveWorkersDialog';
import { WorkerActionsDialog } from './components/WorkerActionsDialog';
import { WorkerActionDialog } from './components/types';

export const WorkersPage: React.FC = () => {
  const [selectedWorkers, setSelectedWorkers] = useState<WorkerProfile[]>([]);
  const [inviteActiveWorkersDialog, setInviteActiveWorkersDialog] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [workerActionDialog, setWorkerActionDialog] = useState<WorkerActionDialog>({
    isOpen: false,
    worker: undefined,
  });

  const { t } = useTranslation();
  const { scope } = useParams<{ scope: WorkerScope }>();

  const {
    fetchWorkers,
    filteredWorkersCount,
    finalizedWorkers,
    workersLoading,
    totalPageCount,
    totalWorkersCount,
  } = useWorkersData({ scope });

  const reset = () => {
    setSelectedWorkers([]);
    fetchWorkers({ page: 1 });
    setResetKey(prev => prev + 1);
  };

  const isEmptyWorkersList = totalWorkersCount === 0 && !workersLoading;

  const handleToggle = (worker: WorkerProfile) => {
    toggleWorkers(worker, selectedWorkers, setSelectedWorkers);
  };

  const openWorkerActionsDialog = (worker: WorkerProfile) => {
    setWorkerActionDialog({ isOpen: true, worker });
  };

  const invitedWorkerColumns = getWorkerColumns({ openActions: openWorkerActionsDialog });
  const activeWorkerColumns = getWorkerColumnsActive({
    handleToggle,
    selectedWorkers,
  });

  const getColumns = () => {
    if (scope === WorkerScope.AVAILABLE) {
      return activeWorkerColumns;
    } else if (scope === WorkerScope.INVITED) {
      return invitedWorkerColumns;
    }
    return invitedWorkerColumns;
  };

  return (
    <Grid item xs={12}>
      <DataTable<WorkerProfile>
        key={`${scope}-${resetKey}`}
        title={t(workersTableTranslations.title[scope])}
        data={finalizedWorkers}
        columns={getColumns()}
        isLoading={workersLoading}
        serverSide
        pageInfo={{
          totalPageCount,
          totalRowCount: totalWorkersCount,
          filteredRowCount: filteredWorkersCount,
        }}
        onRefetchData={(page, filtersArray) => fetchWorkers({ page, filtersArray })}
        selectedRowCount={selectedWorkers.length}
      />
      {scope === WorkerScope.AVAILABLE && (
        <Box display="flex" gap={2} mt={2}>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            disabled={!selectedWorkers.length}
            onClick={() => setSelectedWorkers([])}>
            <Trans>workersScreen.resetSelection</Trans>
          </Button>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            disabled={!selectedWorkers.length}
            onClick={() => setInviteActiveWorkersDialog(true)}>
            <Trans>workersScreen.inviteToJob</Trans>
          </Button>
        </Box>
      )}
      {isEmptyWorkersList && (
        <Box display="flex" alignItems="center" pt={3}>
          <Typography>
            <Trans>{workersTableTranslations.emptyMessage[scope]}</Trans>
          </Typography>
        </Box>
      )}
      {scope === WorkerScope.AVAILABLE && (
        <InviteActiveWorkersDialog
          isOpen={inviteActiveWorkersDialog}
          handleClose={() => setInviteActiveWorkersDialog(false)}
          onSuccess={reset}
          selectedWorkers={selectedWorkers}
        />
      )}
      {scope === WorkerScope.INVITED && (
        <WorkerActionsDialog
          isOpen={workerActionDialog.isOpen}
          worker={workerActionDialog.worker}
          handleClose={() => setWorkerActionDialog({ isOpen: false, worker: undefined })}
        />
      )}
    </Grid>
  );
};
